import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import type { FavoriteItem } from '../services/userPreferencesStorage';
import { loadPreferences, setHomePagePreference, addFavoriteItem, removeFavoriteItem, savePreferences } from '../services/userPreferencesStorage';

/**
 * User preferences state interface
 */
interface UserPreferencesState {
  /** User's preferred home page route, or null for default */
  homePage: string | null;
  /** List of favorite items */
  favorites: FavoriteItem[];
  /** Whether preferences have been loaded from storage */
  isLoaded: boolean;
}

/**
 * User preferences context value interface
 */
interface UserPreferencesContextValue extends UserPreferencesState {
  /** Number of favorites */
  favoritesCount: number;
  /** Set the user's home page preference */
  setHomePage: (route: string) => void;
  /** Clear the user's home page preference */
  clearHomePage: () => void;
  /** Add an item to favorites */
  addFavorite: (item: Omit<FavoriteItem, 'addedAt'>) => void;
  /** Remove an item from favorites */
  removeFavorite: (id: string) => void;
  /** Clear all favorites */
  clearAllFavorites: () => void;
  /** Check if an item is favorited */
  isFavorite: (id: string) => boolean;
}

/**
 * Default context value
 */
const defaultContextValue: UserPreferencesContextValue = {
  homePage: null,
  favorites: [],
  isLoaded: false,
  favoritesCount: 0,
  setHomePage: () => {},
  clearHomePage: () => {},
  addFavorite: () => {},
  removeFavorite: () => {},
  clearAllFavorites: () => {},
  isFavorite: () => false,
};

/**
 * User preferences context
 */
const UserPreferencesContext = createContext<UserPreferencesContextValue>(defaultContextValue);

/**
 * Provider props
 */
interface UserPreferencesProviderProps {
  children: ReactNode;
}

/**
 * User Preferences Provider
 *
 * Manages user preferences state with localStorage persistence.
 * Uses React state instead of Redux per project architecture standards.
 *
 * @example
 * ```tsx
 * // In app root
 * <UserPreferencesProvider>
 *   <App />
 * </UserPreferencesProvider>
 *
 * // In components
 * const { homePage, favorites, setHomePage } = useUserPreferences();
 * ```
 */
export function UserPreferencesProvider({ children }: UserPreferencesProviderProps) {
  const [state, setState] = useState<UserPreferencesState>({
    homePage: null,
    favorites: [],
    isLoaded: false,
  });

  // Load preferences from localStorage on mount
  useEffect(() => {
    const stored = loadPreferences();

    setState({
      homePage: stored.homePage,
      favorites: stored.favorites,
      isLoaded: true,
    });
  }, []);

  // Action: Set home page
  const setHomePage = useCallback((route: string) => {
    setState(prev => ({ ...prev, homePage: route }));
    setHomePagePreference(route);
  }, []);

  // Action: Clear home page
  const clearHomePage = useCallback(() => {
    setState(prev => ({ ...prev, homePage: null }));
    setHomePagePreference(null);
  }, []);

  // Action: Add favorite
  const addFavorite = useCallback((item: Omit<FavoriteItem, 'addedAt'>) => {
    setState(prev => {
      // Check if already exists
      if (prev.favorites.some(f => f.id === item.id)) {
        return prev;
      }

      const newFavorite: FavoriteItem = {
        ...item,
        addedAt: new Date().toISOString(),
      };

      return {
        ...prev,
        favorites: [...prev.favorites, newFavorite],
      };
    });

    addFavoriteItem(item);
  }, []);

  // Action: Remove favorite
  const removeFavorite = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      favorites: prev.favorites.filter(f => f.id !== id),
    }));

    removeFavoriteItem(id);
  }, []);

  // Action: Clear all favorites
  const clearAllFavorites = useCallback(() => {
    setState(prev => {
      const stored = loadPreferences();

      savePreferences({
        ...stored,
        favorites: [],
      });

      return { ...prev, favorites: [] };
    });
  }, []);

  // Utility: Check if item is favorited
  const isFavorite = useCallback((id: string) => state.favorites.some(f => f.id === id), [state.favorites]);

  // Memoized context value
  const contextValue = useMemo<UserPreferencesContextValue>(
    () => ({
      ...state,
      favoritesCount: state.favorites.length,
      setHomePage,
      clearHomePage,
      addFavorite,
      removeFavorite,
      clearAllFavorites,
      isFavorite,
    }),
    [state, setHomePage, clearHomePage, addFavorite, removeFavorite, clearAllFavorites, isFavorite]
  );

  return <UserPreferencesContext.Provider value={contextValue}>{children}</UserPreferencesContext.Provider>;
}

/**
 * Hook to access user preferences context
 *
 * @throws Error if used outside of UserPreferencesProvider
 *
 * @example
 * ```tsx
 * const { homePage, favorites, setHomePage, addFavorite, isFavorite } = useUserPreferencesContext();
 *
 * // Set home page
 * setHomePage('/communities/abc123/moderation');
 *
 * // Add a favorite
 * addFavorite({
 *   id: 'abc123',
 *   type: 'community',
 *   label: 'Halo Studios',
 *   route: '/communities/abc123',
 * });
 *
 * // Check if item is favorited
 * const isFav = isFavorite('abc123');
 * ```
 */
export function useUserPreferencesContext(): UserPreferencesContextValue {
  const context = useContext(UserPreferencesContext);

  if (context === defaultContextValue) {
    throw new Error('useUserPreferencesContext must be used within a UserPreferencesProvider');
  }

  return context;
}

/**
 * Export context for testing purposes
 */
export { UserPreferencesContext };
